import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Loading from '../loading/Loading'
import TagSelector from '../tags/TagSelector'
import PersonaToggle from '../tcairem/PersonaToggle'
import TCAIREMSection from '../tcairem/TCAIREMSection'
import { PERSONAS } from '../tcairem/config'
import { initializeTcairemResources } from '../../reducers/tcairemReducer'
import { initializeTags } from '../../reducers/tagsReducers'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { groupByTypeTag, matchesPersona, matchesSearch } from '../../utils/tcairemFilters'
import { PAGES } from '../navigation/routes'
import { TCAIREMStyle } from '../styles'

const TCAIREMResources = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id: openResourceId } = useParams()
    const { results, loading } = useSelector(state => state.tcairemResources)
    const typeTags = useSelector(state => state.tags.tcairem)

    const [persona, setPersona] = useState(null)
    const [titleFilter, setTitleFilter] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [audienceTags, setAudienceTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [expandedSections, setExpandedSections] = useState({})
    const [typeTagsReady, setTypeTagsReady] = useState(false)
    const browseRef = useRef(null)

    useEffect(() => {
        dispatch(initializeTcairemResources())
        dispatch(initializeTags('tcairem')).then(() => setTypeTagsReady(true))
        dispatch(initializeTags('content'))
        dispatch(initializeTags('experience'))
        dispatch(initializeTags('demographic'))
    }, [dispatch])

    // Deep link: once data is in, expand the owning section(s); the row
    // scrolls itself into view. Unknown ids redirect back to the bare page.
    useEffect(() => {
        if (!openResourceId || results === null) return
        const resource = results.find(r => String(r.id) === String(openResourceId))
        if (!resource) {
            dispatch(setErrorNotification('Resource not found'))
            navigate(PAGES.tcairemResources.path, { replace: true })
            return
        }
        const tagNames = (resource.tcairem_type_tags || []).map(t => t.name ?? t)
        setExpandedSections(previous => {
            const additions = tagNames.filter(name => !previous[name])
            if (additions.length === 0) return previous
            return { ...previous, ...Object.fromEntries(additions.map(name => [name, true])) }
        })
    }, [openResourceId, results, dispatch, navigate])

    const personaTagNames = PERSONAS.find(p => p.key === persona)?.tagNames ?? null
    const visibleResources = useMemo(() => {
        if (results === null) return []
        return results.filter(resource =>
            matchesPersona(resource, personaTagNames) &&
            matchesSearch(resource, { title: titleFilter, contentTags, audienceTags: [...audienceTags, ...experienceTags] })
        )
    }, [results, personaTagNames, titleFilter, contentTags, audienceTags, experienceTags])

    const groups = useMemo(
        () => groupByTypeTag(visibleResources, typeTags, personaTagNames),
        [visibleResources, typeTags, personaTagNames]
    )

    const showBrowse = persona !== null || Boolean(openResourceId)
    const highlightedId = openResourceId ? Number(openResourceId) : null

    const scrollToBrowse = () => browseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const toggleSection = (tagName) =>
        setExpandedSections(previous => ({ ...previous, [tagName]: !previous[tagName] }))

    return (
        <Container sx={TCAIREMStyle.container}>
            <Box sx={TCAIREMStyle.hero}>
                <Box sx={TCAIREMStyle.heroText}>
                    <Typography variant='h3' gutterBottom>{PAGES.tcairemResources.title}</Typography>
                    <Typography>
                        T-CAIREM's own educational resources, designed and delivered
                        by our educational team.
                    </Typography>
                    <Button variant='contained' sx={TCAIREMStyle.browseButton} onClick={scrollToBrowse}>
                        Browse Now
                    </Button>
                    <Typography>
                        If interested in using these free resources, please see
                        our <Link component={RouterLink} to={PAGES.attributionRules.path}>attribution rules</Link>.
                    </Typography>
                </Box>
                <Box
                    component='img'
                    sx={TCAIREMStyle.heroImage}
                    src='/static/logo.svg'
                    alt='T-CAIREM educational resources'
                />
            </Box>

            <Box ref={browseRef}>
                <PersonaToggle selected={persona} onSelect={setPersona} />
            </Box>

            {showBrowse && (
                <>
                    <Box sx={TCAIREMStyle.searchControls}>
                        <TextField
                            label='Search by title'
                            value={titleFilter}
                            onChange={event => setTitleFilter(event.target.value)}
                            fullWidth
                        />
                        <TagSelector
                            tagType='content'
                            label='Content Keywords'
                            selectedTags={contentTags}
                            onChange={setContentTags}
                        />
                        <TagSelector
                            tagType='demographic'
                            label='Audience'
                            selectedTags={audienceTags}
                            onChange={setAudienceTags}
                        />
                        <TagSelector
                            tagType='experience'
                            label='Experience Level'
                            selectedTags={experienceTags}
                            onChange={setExperienceTags}
                        />
                    </Box>

                    {loading && <Loading />}
                    {!loading && results !== null && typeTagsReady && groups.length === 0 && (
                        <Typography>No resources match your filters.</Typography>
                    )}
                    {groups.map(group => (
                        <TCAIREMSection
                            key={group.tagName}
                            tagName={group.tagName}
                            resources={group.resources}
                            expanded={Boolean(expandedSections[group.tagName])}
                            onToggle={() => toggleSection(group.tagName)}
                            highlightedId={highlightedId}
                        />
                    ))}
                </>
            )}
        </Container>
    )
}

export default TCAIREMResources
